// Stripe Elements unificado — usado por:
//   - Onboarding (signup wizard, gateway Stripe)
//   - Manage::Admin::Settings::BillingPayments (agregar tarjeta USD/MXN/EUR)
//
// Carga @stripe/stripe-js dinámicamente, monta CardNumberElement +
// CardExpiryElement + CardCvcElement en los targets, y expone:
//   - submit(event): action lista para `submit->stripe-card-form#submit`
//     (modo standalone — billing). Tokeniza, escribe hidden inputs,
//     `requestSubmit()` del form.
//   - tokenize(): API pública async para invocar desde otro controller
//     padre (modo embedded — onboarding tokeniza solo cuando gateway=stripe).
//     Retorna true si tokenizó OK, false si error (mensaje en errorMessage).

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    publishableKey: String,
    currency:       String,
    fieldPrefix:    String,
  };

  static targets = [
    "cardNumber",
    "cardExpiry",
    "cardCvc",
    "tokenInput",
    "cardIdInput",
    "fundingInput",
    "last4Input",
    "errorMessage",
    "submitButton",
  ];

  async connect() {
    this.tokenized = false;

    if (!this.publishableKeyValue) {
      this.showError("Falta la publishable key de Stripe.");
      return;
    }

    try {
      const { loadStripe } = await import("@stripe/stripe-js");
      this.stripe = await loadStripe(this.publishableKeyValue);
      this.elements = this.stripe.elements({ locale: "es" });

      const baseStyle = {
        base: {
          fontSize: "14px",
          color: "#000",
          fontFamily: "system-ui, -apple-system, sans-serif",
          "::placeholder": { color: "#888" },
        },
        invalid: { color: "#ff2f37" },
      };

      this.cardNumberElement = this.elements.create("cardNumber", { style: baseStyle });
      this.cardNumberElement.mount(this.cardNumberTarget);

      this.cardExpiryElement = this.elements.create("cardExpiry", { style: baseStyle });
      this.cardExpiryElement.mount(this.cardExpiryTarget);

      this.cardCvcElement = this.elements.create("cardCvc", { style: baseStyle });
      this.cardCvcElement.mount(this.cardCvcTarget);
    } catch (error) {
      this.showError(error.message || "Error inicializando Stripe.");
    }
  }

  disconnect() {
    this.cardNumberElement?.destroy();
    this.cardExpiryElement?.destroy();
    this.cardCvcElement?.destroy();
  }

  // Action standalone: usado en billing como `submit->stripe-card-form#submit`.
  // Tokeniza, escribe hiddens, `requestSubmit()`.
  async submit(event) {
    if (this.tokenized) return;

    event.preventDefault();
    const ok = await this.tokenize();
    if (!ok) return;

    this.tokenized = true;
    this.element.requestSubmit();
  }

  // API pública: tokeniza y escribe los 4 hiddens. NO hace requestSubmit.
  // Útil para invocar desde un controller padre (onboarding).
  // Retorna true si OK, false si error (con mensaje en errorMessage).
  async tokenize() {
    if (!this.stripe || !this.cardNumberElement) {
      this.showError("Stripe no está inicializado. Recarga la página.");
      return false;
    }

    this.clearError();
    this.disableSubmit();

    try {
      const result = await this.stripe.createToken(this.cardNumberElement);

      if (result.error) {
        this.showError(result.error.message);
        this.enableSubmit();
        return false;
      }

      const { token } = result;
      this.tokenInputTarget.value   = token.id;
      this.cardIdInputTarget.value  = token.card.id;
      this.fundingInputTarget.value = token.card.funding || "unknown";
      this.last4InputTarget.value   = token.card.last4;
      return true;
    } catch (error) {
      this.showError(error.message || "Error procesando la tarjeta.");
      this.enableSubmit();
      return false;
    }
  }

  showError(message) {
    if (!this.hasErrorMessageTarget) return;
    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.classList.remove("hidden");
  }

  clearError() {
    if (!this.hasErrorMessageTarget) return;
    this.errorMessageTarget.textContent = "";
    this.errorMessageTarget.classList.add("hidden");
  }

  disableSubmit() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = true;
  }

  enableSubmit() {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = false;
    this.tokenized = false;
  }
}
